import {
    provideZoneChangeDetection,
    ErrorHandler,
    APP_INITIALIZER,
    importProvidersFrom,
} from '@angular/core';

import * as Sentry from '@sentry/angular';

import { environment } from './environments/environment';
import { Router } from '@angular/router';
import { NotificationService } from '@progress/kendo-angular-notification';
import { provideQueryDevTools } from '@ngneat/query-devtools';
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withXhr,
    withInterceptors,
} from '@angular/common/http';
import { AngularDateHttpInterceptor } from './app/interceptors/date.interceptor';
import { provideAuth0, authHttpInterceptorFn } from '@auth0/auth0-angular';
import { dateHttpInterceptorFn } from './app/interceptors/date.interceptor.function';
import { unauthenticatedUserInterceptorFn } from './app/interceptors/unauthenticated-user.interceptor.function';
import { AppRoutingModule } from './app/app-routing.module';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { COMMON_KENDO_IMPORTS } from './app/common/common-imports';
import { SharedPlatformModule } from './app/modules/shared-platform/shared-platform.module';
import { AuthenticationModule } from './app/modules/authentication/authentication.module';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { AngularSvgIconPreloaderModule } from 'angular-svg-icon-preloader';
import { AppComponent } from './app/app.component';

Sentry.init({
    dsn: environment.sentry.dsn,
    environment: environment.sentry.environment,
    dataCollection: {
        userInfo: environment.sentry.sendDefaultPii,
        cookies: false,
        httpHeaders: false,
        urlQueryParams: false,
        httpBodies: [],
        stackFrameVariables: false,
    },
});

bootstrapApplication(AppComponent, {
    providers: [
        // Carried over from the previous bootstrapModule() call's
        // applicationProviders. zone.js stays active; this app is not zoneless.
        provideZoneChangeDetection(),
        importProvidersFrom(
            AppRoutingModule,
            BrowserModule,
            BrowserAnimationsModule,
            CommonModule,
            FormsModule,
            // Every argument below must be an NgModule or ModuleWithProviders.
            // importProvidersFrom() rejects standalone components with NG0800,
            // and that throw only fires while ngDevMode is live -- i.e. in
            // unoptimized builds (development, INT1), not in optimized ones.
            // So do NOT spread Kendo's KENDO_* barrel constants in here: as of
            // Kendo 25 those are arrays of standalone components/directives.
            // Use the corresponding *Module class instead.
            ...COMMON_KENDO_IMPORTS,
            SharedPlatformModule,
            AuthenticationModule,
            FontAwesomeModule,
            AngularSvgIconModule.forRoot(),
            AngularSvgIconPreloaderModule.forRoot({
                configUrl: './assets/svg/icons.json',
            }),
        ),
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                showDialog: false,
            }),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => () => { },
            deps: [Sentry.TraceService],
            multi: true,
        },
        NotificationService,
        environment.production ? [] : provideQueryDevTools(),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AngularDateHttpInterceptor,
            multi: true,
        },
        provideAuth0({
            ...environment.auth0,
            httpInterceptor: {
                allowedList: [
                    {
                        uri: `${environment.apiUrl}*`,
                        tokenOptions: {
                            authorizationParams: {
                                audience:
                                    environment.auth0.authorizationParams
                                        .audience,
                                scope: environment.auth0.authorizationParams
                                    .scope,
                            },
                        },
                    },
                ],
            },
        }),
        provideHttpClient(
            withXhr(),
            withInterceptors([
                authHttpInterceptorFn,
                dateHttpInterceptorFn,
                unauthenticatedUserInterceptorFn,
            ]),
        ),
    ],
}).catch((err) => console.error(err));
